import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert
} from '@mui/material';
import { 
  ContentCopy as ContentCopyIcon,
  Check as CheckIcon
} from '@mui/icons-material';

interface ExportResultsProps {
  exportedConfig: any;
  exportError: string | null;
  exportResult: boolean;
  onCopyConfig: () => void;
}

const ExportResults: React.FC<ExportResultsProps> = ({
  exportedConfig,
  exportError,
  exportResult,
  onCopyConfig
}) => {
  const [copied, setCopied] = useState(false);
  
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    if (copied) {
      timeoutId = setTimeout(() => {
        setCopied(false);
      }, 3000); // Reset after 3 seconds
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [copied]);

  const handleCopy = () => {
    onCopyConfig();
    setCopied(true);
  };

  if (!exportedConfig) return null;
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" gutterBottom sx={{ mb: 0 }}>
          Export Results
        </Typography>
        <Button
          variant="outlined"
          size="small"
          onClick={handleCopy}
          startIcon={copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
          color={copied ? "success" : "primary"}
        >
          {copied ? "Copied!" : "Copy Configuration"}
        </Button>
      </Box>
      
      {exportError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {exportError}
        </Alert>
      )}
      
      {exportResult && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Configuration exported successfully!
        </Alert>
      )}
      
      <Box sx={{ 
        maxHeight: '400px', 
        overflow: 'auto', 
        bgcolor: 'background.paper',
        p: 2,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        fontFamily: 'monospace',
        fontSize: '0.875rem'
      }}>
        <pre>{JSON.stringify(exportedConfig, null, 2)}</pre>
      </Box>
    </Box>
  );
};

export default ExportResults;
